import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { persistSession, isAuthenticated } from 'src/common/auth/session';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const bulletItems = [
  {
    icon: <HubOutlinedIcon fontSize="small" />,
    title: 'Multi-tenant control',
    body: 'Operate tenants, platforms, and policies from one hardened console.',
  },
  {
    icon: <VerifiedUserOutlinedIcon fontSize="small" />,
    title: 'Identity-grade posture',
    body: 'Designed for audit trails, least privilege, and operational transparency.',
  },
  {
    icon: <LockOutlinedIcon fontSize="small" />,
    title: 'Superadmin only',
    body: 'Restricted surface aligned with the x-IAM service security model.',
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = useMemo(() => {
    const state = location.state as { from?: string } | null;
    const raw = state?.from;
    if (raw && raw !== '/login') {
      return raw;
    }
    return '/overview';
  }, [location.state]);

  if (isAuthenticated()) {
    return <Navigate to={fromPath} replace />;
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', remember: true },
  });

  const handleFormSubmit = useCallback(
    (values: LoginFormValues) => {
      persistSession({ remember: values.remember });
      navigate(fromPath, { replace: true });
    },
    [fromPath, navigate]
  );

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={(theme) => ({
          position: 'relative',
          display: 'none',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: theme.spacing(6),
          overflow: 'hidden',
          background: `linear-gradient(145deg, ${theme.palette.primary.dark}22 0%, ${theme.palette.background.default} 45%, #0a1424 100%)`,
          borderRight: '1px solid rgba(255,255,255,0.06)',
          [theme.breakpoints.up('md')]: {
            display: 'flex',
            width: '42%',
            minHeight: '100vh',
          },
        })}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 320,
            height: 320,
            borderRadius: '50%',
            filter: 'blur(80px)',
            opacity: 0.35,
            top: '-40px',
            right: '-80px',
            background: 'radial-gradient(circle, #6EA8FE 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.75 }}>
              x-IAM
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mt: 1 }}>
              Superadmin console
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, opacity: 0.82, maxWidth: 420 }}>
              Enterprise control plane for the x-IAM identity and access service across tenants and
              platforms.
            </Typography>
          </Box>
          <Stack spacing={2.5}>
            {bulletItems.map((item) => (
              <Stack key={item.title} direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(110,168,254,0.12)',
                    color: 'primary.light',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.25 }}>
                    {item.body}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Typography variant="caption" sx={{ position: 'relative', zIndex: 1, opacity: 0.55 }}>
          © {new Date().getFullYear()} x-IAM · Superadmin access is monitored.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
          px: 2,
        }}
      >
        <Container maxWidth="sm" sx={{ maxWidth: 440 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
            }}
          >
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              <Typography variant="overline" sx={{ opacity: 0.75 }}>
                Secure sign-in
              </Typography>
              <Typography variant="h5">Welcome back</Typography>
              <Typography variant="body2" sx={{ opacity: 0.78 }}>
                Authenticate with your superadmin credentials to manage x-IAM.
              </Typography>
            </Stack>

            <Alert severity="info" sx={{ mb: 2, alignItems: 'center', borderRadius: 2 }}>
              The console currently loads without an auth gate so you can refine UX. This form still
              stores a local session only — replace it with your x-IAM token exchange when you go live.
            </Alert>

            <Box component="form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
              <Stack spacing={2}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Work email"
                      type="email"
                      autoComplete="username"
                      fullWidth
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: 'Password is required',
                    minLength: { value: 8, message: 'At least 8 characters' },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Password"
                      type="password"
                      autoComplete="current-password"
                      fullWidth
                      error={Boolean(errors.password)}
                      helperText={errors.password?.message}
                    />
                  )}
                />
                <Controller
                  name="remember"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          color="primary"
                        />
                      }
                      label="Keep me signed in on this device"
                    />
                  )}
                />
                <Button type="submit" size="large" fullWidth disabled={isSubmitting}>
                  Sign in to console
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="caption" sx={{ display: 'block', opacity: 0.65, textAlign: 'center' }}>
              Trouble signing in? Contact your platform or security team for access recovery.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
